import { FC, ReactNode, useState } from 'react';
import { motion, Variants } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Button } from 'primereact/button';
import confetti from 'canvas-confetti';

const CELEBRATION_MS = 900;
const fireConfetti = () => {
  const base = {
    particleCount: 90,
    startVelocity: 55,
    spread: 55,
    disableForReducedMotion: true,
  };
  confetti({ ...base, angle: 60, origin: { x: 0, y: 1 } });
  confetti({ ...base, angle: 120, origin: { x: 1, y: 1 } });
  confetti({ ...base, angle: 90, spread: 80, origin: { x: 0.5, y: 1 } });
};

const Tag: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="tw-font-atkinson tw-font-bold tw-text-indigo-600 tw-bg-white tw-rounded-full tw-px-5 tw-py-2 tw-shadow-sm">
    {children}
  </span>
);

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeInOut', delay },
  }),
};

interface Props {
  onFinish: () => void;
}

const TeacherWelcomeSummary: FC<Props> = ({ onFinish }) => {
  const [isFinishing, setIsFinishing] = useState(false);
  const handleFinish = () => {
    if (isFinishing) return;
    setIsFinishing(true);
    fireConfetti();
    window.setTimeout(onFinish, CELEBRATION_MS);
  };

  return (
    <motion.div
      initial="hidden"
      animate="show"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-8 tw-py-[88px] tw-px-6 tw-text-center"
    >
      <motion.p variants={itemVariants} custom={0} className="tw-font-atkinson tw-text-black tw-text-2xl tw-m-0">
        Roadmaps gives you real insight into every student
        <br />
        <span className="tw-font-bold">without adding to your workload</span>
      </motion.p>

      <motion.div variants={itemVariants} custom={0.5} className="tw-flex tw-flex-col tw-items-center tw-gap-y-3">
        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-3">
          <Tag>Skill gaps identified</Tag>
          <Tag>Clear paths to proficiency</Tag>
        </div>
        <Tag>Coherence with core instruction</Tag>
      </motion.div>

      <motion.div variants={itemVariants} custom={1} className="tw-flex tw-flex-col tw-items-center tw-gap-y-2 tw-mt-4">
        <h1 className="tw-font-sora tw-font-bold tw-text-indigo-600 tw-text-xl tw-m-0">
          Standards and curriculum-aligned
        </h1>
        <p className="tw-font-atkinson tw-text-neutral-700 tw-m-0">Mapped to your units, your scope, your classroom.</p>
        <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2">
          <Tag>Unit alignment</Tag>
          <Tag>State standards</Tag>
          <ArrowRight size={20} className="tw-text-neutral-800" />
        </div>
      </motion.div>

      <motion.div variants={itemVariants} custom={2}>
        <Button
          className="tw-flex tw-flex-row-reverse tw-gap-x-2 tw-mt-4"
          label="Go to my dashboard"
          icon={<ArrowRight size={16} className="hover:tw-text-white" />}
          onClick={handleFinish}
          disabled={isFinishing}
        />
      </motion.div>
    </motion.div>
  );
};

export default TeacherWelcomeSummary;
